import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import Notification from '@/components/elements/Notification';
import { STRIPE_PUBLIC_KEY } from '@/constants/constants';
import { usePaywall } from '@/hooks/usePaywall';
import { setPageTitle } from '@/lib/appInsights';
import { PaywallContextType } from '@/types/util.types';
import checkmark from '../assets/checkmark.svg';
import { FaCheckCircle } from 'react-icons/fa';

const Subscribe = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id } = useParams<{ id: string }>();
  const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
  const { error, purchaseArticle, validateCheckout, subscribeToAuthor } = usePaywall() as PaywallContextType;
  const [clientSecret, setClientSecret] = useState('');
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const showTrial = state?.isTrial ?? false;

  /*
   * Setup stripe promise with the public key on first load
   */
  useEffect(() => {
    async function fetchStripe() {
      setStripePromise(await loadStripe(STRIPE_PUBLIC_KEY));
    }
    setPageTitle('Subscribe');
    fetchStripe();
  }, []);

  /**
   * Get the client secret that creates the checkout session
   */
  useEffect(() => {
    async function validateCheckoutAfterSubscribe() {
      setIsLoading(true);
      validateCheckout(checkoutSessionId as string).then((result) => {
        if (result && result.success) {
          setPurchaseSuccess(true);
          setIsLoading(false);
          window.location.assign(`/author/${result.authorId}`);
        } else {
          setIsLoading(false);
        }
      });
      return;
    }

    async function getClientSecret() {
      if (!state || !stripePromise) return;
      const productId = state.productId;
      const authorId = state.authorId;
      const client_secret = await subscribeToAuthor(productId, authorId);
      if (client_secret) {
        setClientSecret(client_secret);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSessionId = urlParams.get('session_id');

    if (checkoutSessionId) {
      validateCheckoutAfterSubscribe();
    } else {
      getClientSecret();
    }
  }, [id, stripePromise, purchaseArticle, validateCheckout, navigate, state, subscribeToAuthor]);

  return (
    <div className='text-center text-[#002327]'>
      {isLoading && (
        <div className='mt-8 mb-12 text-center'>
          <div className='mt-8 mb-8 text-center'>
            <svg
              aria-hidden='true'
              role='status'
              className='inline w-[64px] h-[64px] me-3 text-gray-200 animate-spin dark:text-gray-200'
              viewBox='0 0 100 101'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                fill='currentColor'
              />
              <path
                d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                fill='teal'
              />
            </svg>
          </div>

          <h2 className='font-bold'>Checking your purchase</h2>
        </div>
      )}

      {purchaseSuccess && (
        <div className='mt-8 mb-12 text-center'>
          <div className='mt-8 mb-8 text-center'>
            <img src={checkmark} alt='React Logo' className='h-[64px] w-[64px] m-auto' />
          </div>

          <h2 className='font-bold'>Subscribe Successful</h2>
          <p>Taking you back to the author...</p>
        </div>
      )}

      {error && (
        <div className='mt-8 mb-12 text-center'>
          <h2 className='font-bold'>Purchase Failed</h2>
          <p>
            There was a problem validating your purchase of the article. Please contact a member of our support team.
          </p>

          <div className='max-w-lg mx-auto'>
            <Notification {...error} />
          </div>
        </div>
      )}

      {stripePromise && clientSecret && (
        <div className='flex flex-col md:flex-row w-full'>
          <div className='basis-1/2 text-left text-white bg-[#092B2E] md:min-h-[74vh]'>
            <div className='my-8 md:my-16 px-6 md:w-2/3 mx-auto'>
              {/* <h2 className='font-bold text-4xl'>Start your 7 days free trial</h2> */}
              {showTrial ? (
                <h2 className='font-bold text-4xl'>Start your 7 days free trial</h2>
              ) : (
                <h2 className='font-bold text-4xl'>Subscribe today</h2>
              )}

              <p className='text-xl leading-8'>
                Try us out with no obligations. If eklipX Research doesn&apos;t meet your expectations, you can cancel
                anytime during your trial — no strings attached.
              </p>
              {showTrial && <h4 className='mt-16'>Your Free Trial Includes:</h4>}
              <ul className='mt-0'>
                <li className='flex items-center space-x-4 my-8'>
                  <FaCheckCircle className=' flex-none w-8 h-8' />
                  <span>Get Instant access to all articles from this author</span>
                </li>
                <li className='flex items-center space-x-4 my-8'>
                  <FaCheckCircle className=' flex-none w-8 h-8' />
                  <span>Exclusive reports from this top market analyst.</span>
                </li>
                {showTrial && (
                  <>
                    <li className='flex items-center space-x-4 my-8'>
                      <FaCheckCircle className=' flex-none w-8 h-8' />
                      <span>
                        Cancel at <strong>any time</strong> during your trial
                      </span>
                    </li>

                    <li className='flex items-center space-x-4 my-8'>
                      <FaCheckCircle className=' flex-none w-8 h-8' />
                      <span>
                        Pay <strong>Nothing</strong> for the first 7-days
                      </span>
                    </li>
                  </>
                )}
              </ul>
              <p className='font-bold'>No Commitments! Get Started Now!</p>
              {showTrial && (
                <p className='opacity-70 mt-16'>
                  At the end of your free trial period, you will continue access to this author&apos;s content at the
                  full price displayed above.
                </p>
              )}
            </div>
          </div>
          <div className='basis-1/2 mt-4 mb-4'>
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribe;
